/**
 * Ekip › Ofis (v0.1.222; Sercan, 22 Oct 2026: "ajanların odada çalıştıklarını gösteren sayfalar... masaları odaları
 * olsun, tıklayınca yaptıkları yapacaklarını göreyim"). The company as a floor plan in the panel's drawing style:
 * one room per department (GET /team/organization), one desk per member, a small pixel figure at each desk whose pose
 * follows the live snapshot (typing while it works, a question mark while it waits for a decision, asleep while
 * paused, a red mark on an error, an empty chair for a role that is not switched on yet). A desk opens a drawer with
 * what the member does now, what comes next (its open tasks, GET /people/{id}/activity) and what it did lately.
 * Read only; motion stops under prefers-reduced-motion.
 */

import { panelAgentIdOf } from '@twin/contracts';
import { useEffect, useRef, useState } from 'react';
import type { OrgDepartment, OrgMember } from '../api/organization.js';
import type { Agent } from '../api/types.js';
import { usePanel } from '../app/context.js';
import { useOrganization } from '../app/organization.js';
import { usePerson } from '../app/people.js';
import { Link } from '../app/router.js';
import { SectionHeader } from './primitives.js';

type Pose = 'typing' | 'asking' | 'idle' | 'asleep' | 'error' | 'empty' | 'human';

const POSE_LABEL: Readonly<Record<Pose, string>> = {
  typing: 'Çalışıyor',
  asking: 'Karar bekliyor',
  idle: 'Hazır',
  asleep: 'Duraklatıldı',
  error: 'Hata',
  empty: 'Henüz açılmadı',
  human: 'İnsan',
};

function liveOf(member: OrgMember, agents: readonly Agent[]): Agent | undefined {
  // Organization members carry config ids (icerik-seo, genel-mudur); the snapshot uses panel ids.
  const id = panelAgentIdOf(member.id) ?? member.id;
  return agents.find((agent) => agent.id === id);
}

function poseOf(member: OrgMember, live: Agent | undefined): Pose {
  if (member.kind === 'human') return 'human';
  if (live === undefined) return member.state === 'planned' ? 'empty' : member.state === 'paused' ? 'asleep' : 'idle';
  if (live.state === 'working') return 'typing';
  if (live.state === 'waiting') return 'asking';
  if (live.state === 'paused') return 'asleep';
  if (live.state === 'error') return 'error';
  return 'idle';
}

/** A stable shirt colour per member, from the series palette (the same member always wears the same colour). */
function shirtOf(id: string): string {
  let hash = 0;
  for (const ch of id) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return `var(--series-${(hash % 5) + 1})`;
}

/**
 * The figure: a person seated behind a desk, seen from the front: the head and shoulders above the back of a monitor,
 * the hands at the desk edge. Drawn as 1 x 1 rects so it stays crisp at any size. Map cells: . empty, h hair, s skin,
 * b shirt.
 */
const PERSON = [
  '....hhhh....',
  '...hhhhhh...',
  '...hssssh...',
  '...ssssss...',
  '....ssss....',
  '..bbbbbbbb..',
  '.bbbbbbbbbb.',
  '.bbbbbbbbbb.',
];

function Figure({ pose, shirt }: { pose: Pose; shirt: string }) {
  const fill: Record<string, string> = { h: 'var(--office-hair)', s: 'var(--office-skin)', b: shirt };
  return (
    <svg
      className={`office-fig office-fig--${pose}`}
      viewBox="0 0 12 16"
      aria-hidden="true"
      shapeRendering="crispEdges"
    >
      {/* the chair back, behind the person */}
      <rect x="2" y="3" width="8" height="6" fill="var(--office-chair)" />
      {pose !== 'empty' && (
        <g className="office-body" transform="translate(0 1)">
          {PERSON.map((row, y) =>
            [...row].map((cell, x) =>
              cell === '.' || fill[cell] === undefined ? null : (
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed pixel map
                <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill={fill[cell]} />
              ),
            ),
          )}
        </g>
      )}
      {/* the monitor, its back to us, on the desk in front of the person */}
      <rect className="office-screen" x="3" y="7" width="6" height="4" />
      <rect className="office-led" x="8" y="10" width="1" height="1" />
      <rect className="office-desk" x="0" y="11" width="12" height="2" />
      <rect className="office-desk" x="1" y="13" width="1" height="3" />
      <rect className="office-desk" x="10" y="13" width="1" height="3" />
      {pose !== 'empty' && (
        <g className="office-hands">
          <rect x="1" y="10" width="2" height="1" fill="var(--office-skin)" />
          <rect x="9" y="10" width="2" height="1" fill="var(--office-skin)" />
        </g>
      )}
    </svg>
  );
}

function Bubble({ pose }: { pose: Pose }) {
  const mark =
    pose === 'asking' ? '?' : pose === 'asleep' ? 'z' : pose === 'error' ? '!' : pose === 'typing' ? '…' : null;
  return mark === null ? null : <span className={`office-bubble office-bubble--${pose}`}>{mark}</span>;
}

interface DeskProps {
  member: OrgMember;
  live: Agent | undefined;
  selected: boolean;
  onOpen: () => void;
}

function Desk({ member, live, selected, onOpen }: DeskProps) {
  const pose = poseOf(member, live);
  const line = live?.shortTask || member.role;
  return (
    <button
      type="button"
      className={`office-desk-btn${selected ? ' is-selected' : ''}`}
      data-member={member.id}
      onClick={onOpen}
      aria-pressed={selected}
      aria-label={`${member.name}, ${member.role}: ${POSE_LABEL[pose]}${live?.shortTask ? `, ${live.shortTask}` : ''}`}
    >
      <span className="office-figwrap">
        <Figure pose={pose} shirt={member.kind === 'human' ? 'var(--accent)' : shirtOf(member.id)} />
        <Bubble pose={pose} />
      </span>
      <span className="office-name">{member.name}</span>
      <span className={`office-state office-state--${pose}`}>{POSE_LABEL[pose]}</span>
      <span className="office-line ink-3">{line}</span>
    </button>
  );
}

function Room({
  department,
  agents,
  selected,
  onOpen,
}: {
  department: OrgDepartment;
  agents: readonly Agent[];
  selected: string | null;
  onOpen: (member: OrgMember) => void;
}) {
  const people = [
    ...(department.manager === null ? [] : [department.manager]),
    ...department.members.filter((member) => member.id !== department.manager?.id),
  ];
  const working = people.filter((member) => poseOf(member, liveOf(member, agents)) === 'typing').length;
  return (
    <section
      className={`office-room${department.independent ? ' office-room--independent' : ''}`}
      aria-label={department.name}
    >
      <header className="office-room__head">
        <span className="office-room__name">{department.name}</span>
        <span className="office-room__count mono">
          {working > 0 ? `${working} çalışıyor` : `${people.length} masa`}
        </span>
      </header>
      <p className="office-room__purpose ink-3">{department.purpose}</p>
      <div className="office-room__desks">
        {people.map((member) => (
          <Desk
            key={member.id}
            member={member}
            live={liveOf(member, agents)}
            selected={selected === member.id}
            onOpen={() => onOpen(member)}
          />
        ))}
      </div>
      <span className="office-door" aria-hidden="true" />
    </section>
  );
}

function when(at: number | null, now: number): string {
  if (at === null) return '';
  const minutes = Math.round((at - now) / 60_000);
  if (Math.abs(minutes) < 60) return minutes >= 0 ? `${minutes} dk sonra` : `${-minutes} dk önce`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return hours >= 0 ? `${hours} sa sonra` : `${-hours} sa önce`;
  return new Date(at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

function Drawer({ member, live, onClose }: { member: OrgMember; live: Agent | undefined; onClose: () => void }) {
  const { store, state } = usePerson(member.id);
  const [now] = useState(() => Date.now());
  useEffect(() => {
    void store.ensureLoaded(member.id);
  }, [store, member.id]);
  const pose = poseOf(member, live);
  const view = state.view;
  const heading = useRef<HTMLHeadingElement>(null);
  // Opening a desk moves focus to the drawer; Esc closes it (the Office returns focus to the desk).
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <aside
      className="office-drawer drafting"
      aria-label={`${member.name}: ne yapıyor`}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <header className="office-drawer__head">
        <div>
          <h3 className="office-drawer__name" ref={heading} tabIndex={-1}>
            {member.name}
          </h3>
          <p className="ink-3">{member.role}</p>
        </div>
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          Kapat
        </button>
      </header>
      <p className={`office-state office-state--${pose}`}>{POSE_LABEL[pose]}</p>
      {member.duty && <p className="office-drawer__duty">{member.duty}</p>}

      <h4 className="key ink-2">Şimdi</h4>
      {live ? (
        <ul className="office-list">
          <li>{live.desktopTask || live.task || 'Boşta'}</li>
          {live.progress && (
            <li className="ink-3">
              Adım {live.progress.doneSteps + 1}: {live.progress.currentStep}
            </li>
          )}
          {live.waitingNote && <li className="ink-3">Beklediği: {live.waitingNote}</li>}
          {live.error && <li className="office-err">Hata: {live.error.message}</li>}
          {live.todayLine && <li className="ink-3">Bugün: {live.todayLine}</li>}
        </ul>
      ) : (
        <p className="ink-3">{member.kind === 'human' ? 'Kararlar ve onaylar.' : 'Bu rol henüz çalışmıyor.'}</p>
      )}

      <h4 className="key ink-2">Sıradaki işler</h4>
      <ul className="office-list">
        {live?.next && (
          <li>
            {live.next.label} <span className="ink-3">· {when(live.next.at, now)}</span>
          </li>
        )}
        {(view?.upcoming ?? []).map((task) => (
          <li key={task.id}>
            {task.title} <span className="ink-3">· {task.status}</span>
          </li>
        ))}
        {!live?.next && view !== null && view.upcoming.length === 0 && <li className="ink-3">Bekleyen işi yok.</li>}
        {state.loading && view === null && <li className="ink-3">Yükleniyor…</li>}
      </ul>

      <h4 className="key ink-2">Son yaptıkları</h4>
      <ul className="office-list">
        {(view?.timeline ?? []).slice(0, 6).map((event) => (
          <li key={event.id}>
            {event.link ? <Link to={event.link}>{event.title}</Link> : event.title}{' '}
            <span className="ink-3">
              · {when(event.at, now)}
              {event.status ? ` · ${event.status}` : ''}
            </span>
          </li>
        ))}
        {view !== null && view.timeline.length === 0 && <li className="ink-3">Son 30 günde kayıt yok.</li>}
        {state.error && <li className="office-err">Okunamadı: {state.error}</li>}
      </ul>
      <Link to={`/ekip/kisi/${member.id}`} className="btn btn--secondary">
        Bütün geçmişi
      </Link>
    </aside>
  );
}

export function Office() {
  const { snapshot } = usePanel();
  const { store, state } = useOrganization();
  const [open, setOpen] = useState<OrgMember | null>(null);
  const plan = useRef<HTMLDivElement>(null);
  const close = () => {
    const id = open?.id;
    setOpen(null);
    // Back to the desk the drawer was opened from.
    requestAnimationFrame(() => plan.current?.querySelector<HTMLElement>(`[data-member="${id}"]`)?.focus());
  };
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  const agents = snapshot?.agents ?? [];
  const departments = state.view?.departments ?? [];
  return (
    <section className="office" aria-labelledby="office-title">
      <SectionHeader id="office-title" title="Ofis" />
      <p className="office-hint ink-3">Bir masaya tıkla: şu an ne yaptığı, sıradaki işleri ve son yaptıkları.</p>
      {state.error && departments.length === 0 && (
        <p className="office-err">
          Ofis yüklenemedi.{' '}
          <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
            Tekrar dene
          </button>
        </p>
      )}
      <div className={`office-body-wrap${open ? ' has-drawer' : ''}`}>
        <div className="office-plan" ref={plan}>
          {departments.map((department) => (
            <Room
              key={department.code}
              department={department}
              agents={agents}
              selected={open?.id ?? null}
              onOpen={(member) => setOpen(open?.id === member.id ? null : member)}
            />
          ))}
        </div>
        {open && <Drawer member={open} live={liveOf(open, agents)} onClose={close} />}
      </div>
    </section>
  );
}
